'use client';

import React, { useState } from 'react';
import { signInErrorMessage } from '@/lib/auth-error';
import { useRouter } from 'next/navigation';
import { useWholesalerBranding } from '@/context/WholesalerBrandingContext';
import Link from 'next/link';
import { signIn } from 'next-auth/react';
import toast from 'react-hot-toast';
import { useAppDispatch } from '@/redux/Hook';
import { userLogin, resellerGoogleLogin, resellerGoogleLink, resellerGoogleCompleteSignup } from '@/redux/project/ThunkActions';
import { normalizeMobile } from '@/utils/mobile';
import { MobileCountryInput } from '@/app/shared/auth-layout/mobile-country-input';
import { googleOAuthBridgeLogin } from '@/utils/googleOAuthBridge';
import { AuthGoogleCredentialButton, useGoogleCredentialAvailable } from '@/app/shared/auth-layout/google-credential-button';
import { iErrorResponse } from '@/customTypes/CommonServiceTypes';
import { routes } from '@/config/routes';
import { getPostAuthRedirectPath } from '@/utils/onboarding-setup';
import {
  AuthSplitLayout,
  AuthField,
  AuthPasswordField,
  AuthButton,
  AuthGoogleButton,
  AuthOrRule,
} from '@/app/shared/auth-layout/auth-split-layout';

const GOOGLE_AUTH_ENABLED = process.env.NEXT_PUBLIC_GOOGLE_AUTH_ENABLED === 'true';

export default function SignInScreen() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const { isWhitelabel, brandName, branding } = useWholesalerBranding();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleNotFoundEmail, setGoogleNotFoundEmail] = useState<string | null>(null);
  // An account with the Google email exists but isn't linked yet: ask before linking.
  const [linkPrompt, setLinkPrompt] = useState<{ linkToken: string; email: string; needsPassword: boolean } | null>(null);
  const [linkPassword, setLinkPassword] = useState('');
  const [linkLoading, setLinkLoading] = useState(false);
  // First-time Google sign-in: nothing is created until the person picks an account type and verifies a mobile number.
  type SignupRole = 'VENDOR' | 'WHOLESALER' | 'DEVELOPER';
  const [signupPrompt, setSignupPrompt] = useState<{ signupToken: string; email: string; name: string } | null>(null);
  const [signupRole, setSignupRole] = useState<SignupRole | ''>('');
  const [signupMobile, setSignupMobile] = useState('');
  const [signupBusy, setSignupBusy] = useState(false);
  const signupUrl = isWhitelabel && branding?.retailer_signup_domain
    ? `https://${branding.retailer_signup_domain}/retailer-signup`
    : routes.auth.signUp1;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErr, setFieldErr] = useState<{ email?: string; password?: string }>({});

  const onSubmit = async (data: { email: string; password: string }) => {
    setLoading(true);
    try {
      // @ts-expect-error type mismatch
      const response = await dispatch(userLogin({ ...data }));
      if (response.meta.requestStatus === 'fulfilled') {
        toast.success('Login successful');
        const result = await signIn('credentials', { email: (data as any).email, token: (response as any)?.payload?.data?.token, redirect: false });
        if (result?.error) {
          toast.error(signInErrorMessage(result.error));
          setLoading(false);
          return;
        }
        const onboardingComplete = (response.payload as any)?.data?.user?.onboardingComplete;
        router.replace(getPostAuthRedirectPath('/', onboardingComplete));
      } else {
        const payload = response.payload as iErrorResponse;
        const errorMessage =
          payload?.message?.error?.validationErrors?.body?.message ||
          payload?.message?.error?.message ||
          'An unknown error occurred';
        toast.error(errorMessage);
        setLoading(false);
      }
    } catch {
      toast.error('Something went wrong');
      setLoading(false);
    }
  };

  // Google sign-in runs through the popup bridge so it also works on white-label domains.
  // On admin.cresivox.com the one-click Google button returns an ID token directly; elsewhere we use the bridge.
  const googleCredentialOk = useGoogleCredentialAvailable();
  const handleGoogleSignIn = () =>
    runGoogleLogin(async () => {
      const popup = await googleOAuthBridgeLogin();
      return popup ? { code: popup.code } : null;
    });
  const handleGoogleCredential = (credential: string) => runGoogleLogin(async () => ({ credential }));

  async function runGoogleLogin(getPayload: () => Promise<{ code?: string; credential?: string } | null>) {
    if (googleLoading) return;
    setGoogleLoading(true);
    setGoogleNotFoundEmail(null);
    setLinkPrompt(null);
    setSignupPrompt(null);
    try {
      const googlePayload = await getPayload();
      if (!googlePayload) {
        toast.error('Google sign-in was cancelled or blocked. Allow pop-ups and try again.');
        return;
      }
      toast.loading('Verifying...', { id: 'google-login' });
      // autoSignup: a Google email with no account becomes a new (free) reseller account.
      const response = await dispatch(resellerGoogleLogin({ ...googlePayload, autoSignup: true }));
      if (response.meta.requestStatus !== 'fulfilled') {
        const apiError = (response.payload as any)?.message?.error;
        if (apiError?.error === 'GOOGLE_ACCOUNT_NOT_FOUND') {
          // No account for this Google email: offer sign-up instead of a dead end.
          toast.dismiss('google-login');
          setGoogleNotFoundEmail(apiError.email || '');
        } else if (apiError?.error === 'GOOGLE_SIGNUP_DETAILS_REQUIRED' && apiError.signupToken) {
          toast.dismiss('google-login');
          setSignupRole('');
          setSignupMobile('');
          setSignupPrompt({ signupToken: apiError.signupToken, email: apiError.email || '', name: apiError.name || '' });
        } else if (apiError?.error === 'GOOGLE_LINK_CONFIRM_REQUIRED' && apiError.linkToken) {
          toast.dismiss('google-login');
          setLinkPassword('');
          setLinkPrompt({ linkToken: apiError.linkToken, email: apiError.email || '', needsPassword: Boolean(apiError.needsPassword) });
        } else {
          const body = (response.payload as any)?.message;
          toast.error(apiError?.message || body?.message || 'Could not sign in with Google.', { id: 'google-login' });
        }
        return;
      }
      await finishGoogleSession(response.payload as any);
    } catch {
      toast.error('An error occurred during Google sign-in.', { id: 'google-login' });
    } finally {
      setGoogleLoading(false);
    }
  }

  // Shared by Google sign-in, auto sign-up and "link existing account": start the NextAuth session and route onward.
  async function finishGoogleSession(payload: any, createdAs?: string) {
    const data = payload?.data;
    const result = await signIn('credentials', { email: data?.user?.email || '', token: data?.token, redirect: false });
    if (result?.error) {
      toast.error(signInErrorMessage(result.error), { id: 'google-login' });
      return;
    }
    const createdNow = payload?.httpStatusCode === 201;
    toast.success(createdNow ? 'Account created with Google!' : 'Login successful', { id: 'google-login' });
    if (createdNow && typeof window !== 'undefined') {
      const w = window as any;
      if (w.fbq) w.fbq('track', 'CompleteRegistration', { content_name: createdAs || 'reseller', currency: 'INR', value: 2499 });
      if (w.gtag) {
        w.gtag('event', 'sign_up', { method: 'google' });
        w.gtag('event', 'conversion', { send_to: 'G-3XGHWEKW6E', currency: 'INR', value: 2499 });
      }
    }
    router.replace(getPostAuthRedirectPath('/', data?.user?.onboardingComplete));
  }

  async function completeGoogleSignup() {
    if (!signupPrompt || signupBusy) return;
    const mobile = normalizeMobile(signupMobile.startsWith('+') ? signupMobile : `+${signupMobile}`);
    if (!signupRole) return void toast.error('Choose an account type.');
    if (!mobile) return void toast.error('Enter a valid mobile number with its country code.');
    setSignupBusy(true);
    try {
      toast.loading('Creating your account...', { id: 'google-login' });
      const response = await dispatch(
        resellerGoogleCompleteSignup({ signupToken: signupPrompt.signupToken, role: signupRole, mobile }),
      );
      if (response.meta.requestStatus !== 'fulfilled') {
        const body = (response.payload as any)?.message;
        const apiError = body?.error;
        toast.error(apiError?.message || body?.message || 'Could not create your account.', { id: 'google-login' });
        if (apiError?.error === 'GOOGLE_SIGNUP_EXPIRED') setSignupPrompt(null);
        return;
      }
      setSignupPrompt(null);
      await finishGoogleSession(response.payload as any, signupRole);
    } catch {
      toast.error('An error occurred while creating your account.', { id: 'google-login' });
    } finally {
      setSignupBusy(false);
    }
  }

  async function confirmGoogleLink() {
    if (!linkPrompt || linkLoading) return;
    if (linkPrompt.needsPassword && !linkPassword) {
      toast.error('Enter your account password to link Google.');
      return;
    }
    setLinkLoading(true);
    try {
      toast.loading('Linking...', { id: 'google-login' });
      const response = await dispatch(resellerGoogleLink({ linkToken: linkPrompt.linkToken, password: linkPassword || undefined }));
      if (response.meta.requestStatus !== 'fulfilled') {
        const body = (response.payload as any)?.message;
        const apiError = body?.error;
        toast.error(apiError?.message || body?.message || 'Could not link Google to your account.', { id: 'google-login' });
        // An expired confirmation can't be retried: go back to the start.
        if (apiError?.error === 'GOOGLE_LINK_EXPIRED') setLinkPrompt(null);
        return;
      }
      setLinkPrompt(null);
      await finishGoogleSession(response.payload as any);
    } catch {
      toast.error('An error occurred while linking Google.', { id: 'google-login' });
    } finally {
      setLinkLoading(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const n: { email?: string; password?: string } = {};
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) n.email = 'Enter a valid email address.';
    if (!password) n.password = 'Enter your password.';
    setFieldErr(n);
    if (Object.keys(n).length) return;
    onSubmit({ email, password });
  }

  return (
    <AuthSplitLayout mode="in" toast={toastMsg}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <header>
          <div className="auth-kicker">Welcome back</div>
          <h1 className="auth-display" style={{ fontSize: 40, marginTop: 14 }}>
            Sign in to your<br /><em style={{ fontStyle: 'italic' }}>workspace</em>.
          </h1>
          <p style={{ margin: '14px 0 0', color: 'var(--muted)', fontSize: 15, lineHeight: 1.5 }}>
            Build, sell, and extend your storefront from one place.
          </p>
        </header>

        <AuthField
          id="email"
          label="Email"
          type="email"
          placeholder="you@studio.com"
          autoComplete="email"
          value={email}
          error={fieldErr.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setEmail(e.target.value); setFieldErr(f => ({ ...f, email: undefined })); }}
        />

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <label className="auth-field-label" htmlFor="password" style={{ marginBottom: 7 }}>Password</label>
            <Link href={routes.auth.forgotPassword1} className="auth-linklike" style={{ fontSize: 12.5 }}>Forgot?</Link>
          </div>
          <AuthPasswordField
            id="password"
            value={password}
            error={fieldErr.password}
            autoComplete="current-password"
            onChange={(e) => { setPassword(e.target.value); setFieldErr(f => ({ ...f, password: undefined })); }}
          />
        </div>

        <AuthButton type="submit" loading={loading}>
          Sign in
          <ArrowRight />
        </AuthButton>

        {GOOGLE_AUTH_ENABLED && (
          <>
            <AuthOrRule />
            {googleCredentialOk ? (
              <AuthGoogleCredentialButton
                onCredential={handleGoogleCredential}
                onError={() => toast.error('Google sign-in was cancelled. Please try again.')}
                disabled={googleLoading}
              />
            ) : (
              <AuthGoogleButton onClick={handleGoogleSignIn} />
            )}
            {signupPrompt && (
              <div role="alert"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); completeGoogleSignup(); } }}
                style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, border: '1px solid var(--line)', borderRadius: 12, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600 }}>Finish creating your account</div>
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                  No account exists for {signupPrompt.email}. Choose your account type and enter your mobile number.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {([
                    ['WHOLESALER', 'Build your market palace', 'List catalogs and reach merchants as a supplier.'],
                    ['DEVELOPER', 'Develop for Cresivox', 'Ship plugins and themes with API access.'],
                  ] as [SignupRole, string, string][]).map(([id, label, desc]) => (
                    <label key={id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', border: `1px solid ${signupRole === id ? 'var(--ink, #111)' : 'var(--line)'}`, borderRadius: 10, cursor: 'pointer' }}>
                      <input type="radio" name="google-signup-role" checked={signupRole === id} onChange={() => setSignupRole(id)} style={{ marginTop: 4 }} />
                      <span><strong>{label}</strong><br /><span style={{ fontSize: 13, color: 'var(--muted)' }}>{desc}</span></span>
                    </label>
                  ))}
                </div>
                <MobileCountryInput id="google-signup-mobile" value={signupMobile} onChange={setSignupMobile} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <AuthButton type="button" loading={signupBusy} onClick={completeGoogleSignup}>Create account</AuthButton>
                  <button type="button" className="auth-btn auth-btn-ghost" onClick={() => setSignupPrompt(null)} disabled={signupBusy}>Cancel</button>
                </div>
              </div>
            )}
            {linkPrompt && (
              <div role="alert"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); confirmGoogleLink(); } }}
                style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, border: '1px solid var(--line)', borderRadius: 12, lineHeight: 1.5 }}>
                <div style={{ fontWeight: 600 }}>An account already exists for {linkPrompt.email}</div>
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>
                  Link your Google account to it? You can then sign in with Google next time.
                  {linkPrompt.needsPassword ? ' Enter your current password to confirm it is yours.' : ''}
                </div>
                {linkPrompt.needsPassword && (
                  <AuthPasswordField
                    id="google-link-pw"
                    label="Current password"
                    autoComplete="current-password"
                    value={linkPassword}
                    onChange={(e) => setLinkPassword(e.target.value)}
                  />
                )}
                <div style={{ display: 'flex', gap: 10 }}>
                  <AuthButton type="button" loading={linkLoading} onClick={confirmGoogleLink}>Link and sign in</AuthButton>
                  <button type="button" className="auth-btn auth-btn-ghost" onClick={() => setLinkPrompt(null)} disabled={linkLoading}>Cancel</button>
                </div>
              </div>
            )}
            {googleNotFoundEmail !== null && (
              <div role="alert" className="auth-err" style={{ display: 'block', lineHeight: 1.5 }}>
                No account found{googleNotFoundEmail ? ` for ${googleNotFoundEmail}` : ''}.{' '}
                <Link href={`${signupUrl}${signupUrl.includes('?') ? '&' : '?'}type=reseller`} className="auth-linklike">Sign up as a reseller</Link>
                {' · '}
                <Link href={`${signupUrl}${signupUrl.includes('?') ? '&' : '?'}type=supplier`} className="auth-linklike">Register as a supplier</Link>
              </div>
            )}
          </>
        )}

        <p style={{ margin: 0, fontSize: 14.5, color: 'var(--muted)' }}>
          {isWhitelabel ? `New to ${brandName}?` : 'New to cresivox?'}{' '}
          <Link href={signupUrl} className="auth-linklike">Create an account</Link>
        </p>
      </form>
    </AuthSplitLayout>
  );
}

function ArrowRight() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
